import { UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import {
  SearchItineraryQuery,
  SearchItineraryQueryVariables,
} from 'generated/graphql';
import type { SearchQueryResult } from './types';
import {
  arrayToMap,
  getCommercialCarrier,
} from './transformItinerarySearchResponse';

type SearchItineraryResponse = UseQueryResult<
  SearchItineraryQuery,
  SearchItineraryQueryVariables
>;

// This function takes the unbundle part of the response and
// parses it like it was a regular One Way response
export const transformSearchItineraryUnbundleResponse = (
  {
    data,
    hasCommercialCarriers = false,
  }: {
    data: SearchItineraryResponse['data'];
    hasCommercialCarriers?: boolean;
  },
  { selectedDepartureId }: { selectedDepartureId: string | null }
): SearchQueryResult['data'] => {
  const { searchItinerary } = data || {};
  if (!searchItinerary) return null;

  const {
    insuranceOffers,
    carbonFootprints,
    campaignConfigs,
    locations,
    carriers,
    priceTypeDisplayed,
    sections,
    segments,
  } = searchItinerary;
  // Turn arrays into id maps so we can easily access them by id instead
  // of iterating through the array every time we need a value
  const itinerarySearchMap = {
    ...searchItinerary,
    locations: arrayToMap(locations, 'location'),
    carriers: arrayToMap(carriers, 'carrier'),
    insuranceOffers: arrayToMap(insuranceOffers, 'insuranceOffer'),
    carbonFootprints: arrayToMap(carbonFootprints, 'carbonFootprint'),
    campaignConfigs: arrayToMap(campaignConfigs, 'campaignConfig'),
    sections: arrayToMap(sections, 'section'),
    segments: arrayToMap(segments, 'segment'),
  };

  const mapSegment = (segment) => {
    const segmentCommerciaBrandSegment = segment.sections.reduce(
      (_, section) => {
        return section.commercialBrandSegment;
      }
    );
    return {
      ...segment,
      __typename: 'Segment',
      sections: segment.sections.map((sectionId) => {
        const section = itinerarySearchMap.sections[sectionId];
        const {
          departureId,
          destinationId,
          carrierId,
          operatingCarrierId,
          commercialBrandId,
          insuranceOfferId,
          ...rest
        } = section;
        return {
          ...rest,
          __typename: 'Section',
          insuranceOffer:
            insuranceOfferId != null
              ? itinerarySearchMap.insuranceOffers[insuranceOfferId]
              : null,
          departure: itinerarySearchMap.locations[departureId],
          destination: itinerarySearchMap.locations[destinationId],
          carrier:
            itinerarySearchMap.carriers[
              getCommercialCarrier(
                hasCommercialCarriers,
                carrierId,
                commercialBrandId
              )
            ],
          operatingCarrier:
            itinerarySearchMap.carriers[operatingCarrierId as string],
          commercialBrand:
            itinerarySearchMap.carriers[commercialBrandId as string],
        };
      }),
      carrier:
        itinerarySearchMap.carriers[
          getCommercialCarrier(
            hasCommercialCarriers,
            segment.carrierId,
            segmentCommerciaBrandSegment
          )
        ],
    };
  };

  return {
    search: {
      __typename: 'SearchResponse',
      searchId: searchItinerary.searchId,
      defaultFeeType: { id: priceTypeDisplayed as string },
      itineraries: selectedDepartureId
        ? // This part is for returns
          searchItinerary.unbundledMapping[
            selectedDepartureId
          ].compatibleInboundSegments.map((itinerary, index) => {
            const bundledItinerary =
              searchItinerary.itineraries[itinerary.itineraryKey];
            const segment =
              itinerarySearchMap.segments[itinerary.inboundSegmentId];

            return {
              __typename: 'Itinerary',
              key: index.toString(),
              id: index.toString(),
              // isFareUpgradeAvailable and hotelXSellingEnabled have been deprecated
              // in the back-end
              isFareUpgradeAvailable: false,
              hotelXSellingEnabled: false,
              campaignConfig: null,
              carbonFootprint: null,
              ticketsLeft: segment.ticketsLeft,
              transportTypes: segment.transportTypes,
              meRating: bundledItinerary.meRating,
              fees: bundledItinerary.fees.map((fee) => ({
                price: fee.price,
                type: { id: fee.type },
              })),
              legs: [
                {
                  segmentKeys: [0],
                  segments: [mapSegment(segment)],
                },
              ],
            };
          })
        : // This part is for departures
          searchItinerary?.unbundledMapping.map((itinerary, index) => {
            const segment =
              itinerarySearchMap.segments[itinerary.outboundSegmentId];

            return {
              __typename: 'Itinerary',
              key: index.toString(),
              id: index.toString(),
              // isFareUpgradeAvailable and hotelXSellingEnabled have been deprecated
              // in the back-end
              isFareUpgradeAvailable: false,
              hotelXSellingEnabled: false,
              campaignConfig: null,
              carbonFootprint: null,
              ticketsLeft: segment.ticketsLeft,
              fees: itinerary.cheapestFees.map((fee) => ({
                price: fee.price,
                type: { id: fee.type },
              })),
              transportTypes: segment.transportTypes,
              meRating: itinerary.bestMeRating,
              legs: [
                {
                  __typename: 'Leg',
                  segmentKeys: [0],
                  segments: [mapSegment(segment)],
                },
              ],
            };
          }),
    },
  };
};
